import { motion } from "framer-motion";
import { Droplets, Heart, Gem, Building2 } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

const cards = [
  { num: "01", icon: Droplets, title: "Unwind", subtitle: "Leave the day's rush behind",
    desc: "Settle into a calm, private wellness environment designed for deep rest — ambient light, quiet air and time that belongs only to the two of you.",
    image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
  { num: "02", icon: Heart, title: "Reconnect", subtitle: "Uninterrupted time together",
    desc: "Make space for each other, away from work, traffic and screens. A room where the conversation can finally slow down.",
    image: "https://images.unsplash.com/photo-1600334129128-685c5582fd35?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
  { num: "03", icon: Gem, title: "Indulge", subtitle: "A luxury wellness atmosphere",
    desc: "Enjoy a premium Epsom experience with thoughtful service and a refined, intimate setting across Western Mumbai.", image: null },
  { num: "04", icon: Building2, title: "The Sanctuary", subtitle: "Modern wellness. Luxury service.",
    desc: "Epsom's offering is built around premium wellness, recovery, aesthetics and luxury spa experiences — with a personalised touch for every couple.",
    image: "https://images.unsplash.com/photo-1665824249476-a7951ab0aaaa?crop=entropy&cs=srgb&fm=jpg&w=600&q=80" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.7, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Experience() {
  return (
    <section id="experience" data-testid={TEST_IDS.experienceSection} className="py-20 sm:py-28 bg-[#0d0b09]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-14">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#f2cf8b] mb-3">The Experience</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-[46px] font-semibold text-[#f8f0df] tracking-tight max-w-lg leading-tight">
            60 minutes. <em className="italic text-[#bdb3a4]">Just the two of you.</em>
          </h2>
          <p className="text-base sm:text-lg text-[#bdb3a4] leading-relaxed mt-4 max-w-xl">
            A premium couple wellness experience designed around relaxation, reconnection and quality time — in a private, luxury sanctuary setting.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cards.map((card, i) => (
            <motion.div key={card.num} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
              className={`card-hover group relative border border-[rgba(242,207,139,0.18)] rounded-2xl overflow-hidden ${
                card.image ? "min-h-[340px]" : "p-8 bg-gradient-to-b from-[rgba(255,255,255,0.045)] to-[rgba(255,255,255,0.018)]"
              }`}>
              {card.image ? (
                <>
                  <img src={card.image} alt={card.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d0b09]/95 via-[#0d0b09]/50 to-transparent" />
                  <div className="relative z-10 h-full flex flex-col justify-end p-8">
                    <span className="text-xs font-mono text-[#d7a85c] mb-2">{card.num} · {card.title}</span>
                    <h3 className="font-serif text-xl sm:text-2xl font-medium text-[#f8f0df] mb-2">{card.subtitle}</h3>
                    <p className="text-sm text-[#bdb3a4] leading-relaxed max-w-md">{card.desc}</p>
                  </div>
                </>
              ) : (
                <div className="flex flex-col justify-center h-full">
                  <div className="w-12 h-12 rounded-xl bg-[#d7a85c]/10 flex items-center justify-center mb-5">
                    <card.icon className="w-6 h-6 text-[#d7a85c]" />
                  </div>
                  <span className="text-xs font-mono text-[#bdb3a4] mb-2">{card.num} · {card.title}</span>
                  <h3 className="font-serif text-xl sm:text-2xl font-medium text-[#f8f0df] mb-3">{card.subtitle}</h3>
                  <p className="text-sm text-[#bdb3a4] leading-relaxed">{card.desc}</p>
                  <div className="flex flex-wrap gap-2 mt-6">
                    {["60-Min Session", "Premium Setting", "Only 20 Slots"].map((p) => (
                      <span key={p} className="px-3 py-1 rounded-full text-xs font-medium text-[#ded4c4] border border-[rgba(242,207,139,0.18)]">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
